"use client"

import { useEffect, useState } from "react"
import { usePathname, useRouter } from "next/navigation"
import { MessageSquare, BookOpen, Home, GraduationCap, Wrench, CalendarDays, Sun, CalendarCheck } from "lucide-react"
import { createClient } from "@/lib/supabase/client"
import { Button } from "@/components/ui/button"

const GRADIENTS: Record<string, string> = {
  cyan: "bg-gradient-to-br from-cyan-500 to-blue-500",
  purple: "bg-gradient-to-br from-purple-500 to-indigo-500",
  pink: "bg-gradient-to-br from-pink-500 to-purple-500",
  orange: "bg-gradient-to-br from-orange-500 to-yellow-500",
  emerald: "bg-gradient-to-br from-emerald-500 to-green-500",
  blue: "bg-gradient-to-br from-blue-600 to-sky-500",
  amber: "bg-gradient-to-br from-amber-500 to-orange-500",
  indigo: "bg-gradient-to-br from-indigo-500 to-violet-500",
}

const BREVET_GRADES = /3[eè]me|3e|^3$/i
const BAC_GRADES = /premi[eè]re|1[eè]re|1re|terminale|tle/i

interface AppFooterProps {
  isAdmin?: boolean
  studentGrade?: string | null
}

export function AppFooter({ isAdmin = false, studentGrade }: AppFooterProps) {
  const pathname = usePathname()
  const router = useRouter()

  const [autoGrade, setAutoGrade] = useState<string | null>(studentGrade ?? null)
  const [declicEnabled, setDeclicEnabled] = useState(false)
  const [isFormateur, setIsFormateur] = useState(false)

  useEffect(() => {
    if (studentGrade) setAutoGrade(studentGrade)
    const supabase = createClient()
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) return
      supabase.from("profiles").select("student_grade, declic_enabled, role, is_admin").eq("id", user.id).maybeSingle()
        .then(({ data }) => {
          if (!studentGrade && data?.student_grade) setAutoGrade(data.student_grade)
          setDeclicEnabled(data?.declic_enabled === true)
          setIsFormateur(data?.role === "formateur" || data?.is_admin === true)
        })
    })
  }, [studentGrade])

  // L'espace formateur « Ma classe » a sa propre barre (retour au menu) : la navigation élève
  // n'y a pas sa place et cacherait la dernière rangée d'élèves sur téléphone.
  if (pathname === "/formateur" || pathname?.startsWith("/formateur/")) return null

  const showBrevet = autoGrade ? BREVET_GRADES.test(autoGrade) : false
  const showBac = autoGrade ? BAC_GRADES.test(autoGrade) : false
  const showExam = isAdmin || showBrevet || showBac
  const isBac = autoGrade ? BAC_GRADES.test(autoGrade) : isAdmin

  const NAV_ITEMS = [
    // « Ma classe » en premier pour un formateur ou un administrateur, comme dans la barre du haut (Yann 14/10).
    ...(isFormateur ? [{ icon: CalendarCheck, label: "Ma classe", path: "/formateur", color: "indigo" }] : []),
    // DÉCLIC lot 2 : « Aujourd'hui » remplace l'accueil en premier (spec § 2.1) ; l'accueil reste
    // dans la barre du haut et le menu du compte. Six entrées au plus sur 360 px.
    declicEnabled
      ? { icon: Sun, label: "Aujourd'hui", path: "/aujourdhui", color: "amber" }
      : { icon: Home, label: "Accueil", path: "/dashboard", color: "cyan" },
    { icon: BookOpen,      label: "Matières",  path: "/matieres",  color: "purple" },
    ...(showExam
      ? [{
          icon: GraduationCap,
          label: isBac ? "Bac" : "Brevet",
          path: isBac ? "/bac" : "/brevet",
          color: "emerald",
        }]
      : []),
    { icon: MessageSquare, label: "Tuteur",    path: "/assistant", color: "pink" },
    { icon: Wrench,        label: "Outils",    path: "/outils",    color: "orange" },
    // DÉCLIC lot 1 : « Ma semaine », élèves DÉCLIC seulement.
    ...(declicEnabled ? [{ icon: CalendarDays, label: "Semaine", path: "/ma-semaine", color: "blue" }] : []),
  ] as const
  // Six entrées tiennent sur 360 px si chacune passe de 64 à 56 px ; sept (formateur DÉCLIC) à 48 px.
  const largeur = NAV_ITEMS.length > 6 ? "w-12 sm:w-20" : NAV_ITEMS.length > 5 ? "w-14 sm:w-20" : "w-16 sm:w-20"

  const isActive = (path: string) => {
    if (path === "/dashboard") {
      return pathname === "/dashboard" || pathname === "/"
    }
    return pathname === path || pathname.startsWith(path + "/")
  }

  // Page d'accueil : visiteur non connecté (les connectés sont redirigés), pas de barre d'app.
  if (pathname === "/") return null

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 backdrop-blur-md bg-background/90 border-t border-white/10 pb-safe lg:hidden">
      <div className="flex items-center justify-around px-2 py-2 max-w-2xl mx-auto">
        {NAV_ITEMS.map((item) => {
          const active = isActive(item.path)
          const Icon = item.icon

          return (
            <Button
              key={item.path}
              variant="ghost"
              className={`flex flex-col items-center justify-center h-14 ${largeur} sm:h-16 gap-0.5 sm:gap-1 hover:bg-white/5 rounded-xl transition-all ${
                active ? "scale-105" : ""
              }`}
              onClick={() => router.push(item.path)}
            >
              <div className={`rounded-xl p-1.5 sm:p-2 ${active ? GRADIENTS[item.color] : "bg-white/5"}`}>
                <Icon className="size-4 sm:size-5 text-white" />
              </div>
              <span className={`text-[10px] sm:text-[11px] font-medium ${active ? "text-white" : "text-white/50"}`}>
                {item.label}
              </span>
            </Button>
          )
        })}
      </div>
    </div>
  )
}
